// Shared text helpers used by the global search and by every list-page filter.

// Lowercase, strip Latin diacritics, keep letters/marks/numbers (marks keep Hindi matras intact).
export const normalize = (value = "") =>
  String(value)
    .toLocaleLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\p{L}\p{M}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

// URLs, absolute paths and image/video files are useless for text search.
const SKIP_VALUE =
  /^(?:https?:|data:|\/|\.{1,2}\/)|\.(?:png|jpe?g|webp|svg|gif|avif|mp4|pdf|pptx?)(?:$|[?#])/i;

// Technical keys that only add noise (ids, flags, timestamps...).
const SKIP_KEY =
  /^(?:id|slug|status|order|sort|position|type|.*_id|created.*|updated.*|is_.*)$/i;

const stripHtml = (value) => value.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ");

// Collect every readable string/number from a record (nested objects and arrays too).
export const flattenText = (value, depth = 0, out = []) => {
  if (value == null || depth > 3) return out;

  if (typeof value === "string") {
    const text = value.trim();
    if (text && !SKIP_VALUE.test(text)) out.push(stripHtml(text));
  } else if (typeof value === "number") {
    out.push(String(value));
  } else if (Array.isArray(value)) {
    value.forEach((item) => flattenText(item, depth + 1, out));
  } else if (typeof value === "object" && !value.$$typeof) {
    Object.entries(value).forEach(([key, item]) => {
      if (!SKIP_KEY.test(key)) flattenText(item, depth + 1, out);
    });
  }

  return out;
};

// Cache the normalized text per object so typing stays fast on long lists.
const textCache = new WeakMap();

export const getSearchText = (item) => {
  if (item && typeof item === "object") {
    if (textCache.has(item)) return textCache.get(item);
    const text = normalize(flattenText(item).join(" "));
    textCache.set(item, text);
    return text;
  }
  return normalize(item);
};

// True when every word of the query appears somewhere inside the item.
export const matchesQuery = (item, rawQuery) => {
  const terms = normalize(rawQuery).split(" ").filter(Boolean);
  if (terms.length === 0) return true;
  const text = getSearchText(item);
  return terms.every((term) => text.includes(term));
};